import {
    createStyles,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemSecondaryAction,
    ListItemText,
    makeStyles,
} from "@material-ui/core";
import { format } from "date-fns";
import * as React from "react";

import {
    Feedback,
    FeedbackDetails,
    FeedbackModal,
    FeedbackTypeEnum,
    NoFeedback,
} from ".";
import {
    Button,
    Divider,
    LayoutContainer,
    RelodeTheme,
    Text,
    ThumbsDownIcon,
    ThumbsUpIcon,
    useNotifications,
} from "../../";
import { ClientUserRole } from "../../domain";

const NOT_SHARED_JOB_MESSAGE =
    "You are not shared to this job. You must be shared to this job to give feedback.";
const JOB_NOT_CORRECT_STATUS_MESSAGE =
    "Feedback can only be given when a candidate is in Submitted or Interviewing status.";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
            },
            provideFeedbackContainer: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            },
            info: {
                padding: theme.spacing(3),
                textAlign: "center",
            },
            provideFeedbackButton: {
                paddingRight: theme.spacing(3),
                paddingLeft: theme.spacing(3),
            },
            feedbackList: {
                padding: 0,
            },
            feedbackItem: {
                paddingTop: 0,
            },
            buttonContainer: {
                marginRight: theme.spacing(1),
            },
            iconButton: {
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: 24,
                "&:disabled": {
                    opacity: 0.35,
                },
            },
            feedbackSecondary: {
                top: theme.spacing(3),
            },
            comment: {
                color: theme.relode.colors.label.main,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
            },
            label: {
                color: theme.relode.colors.label.main,
            },
            italicLabel: {
                color: theme.relode.colors.label.main,
                fontStyle: "italic",
            },
            divider: {
                margin: theme.spacing(2, 0, 2, 0),
            },
        }),
    { name: "CandidateFeedback" }
);

interface CandidateFeedbackProps {
    candidateFullName: string;
    feedbackDetails: FeedbackDetails;
    addOrUpdateFeedback?: (comment: string, vote: boolean) => void;
}
export const CandidateFeedback = (props: CandidateFeedbackProps) => {
    const classes = useStyles({});
    const { actions: notifications } = useNotifications();
    const { candidateFullName, feedbackDetails, addOrUpdateFeedback } = props;
    const { feedback: feedbackList, clientsWithoutFeedback } = feedbackDetails;
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] =
        React.useState<boolean>(false);

    let selectedFeedbackType = undefined;
    if (feedbackDetails.clientFeedback) {
        selectedFeedbackType = feedbackDetails.clientFeedback.vote
            ? FeedbackTypeEnum.Positive
            : FeedbackTypeEnum.Negative;
    }

    const handleConfirmFeedback = (comment: string, vote: boolean) => {
        if (
            comment !== feedbackDetails?.clientFeedback?.comment ||
            vote !== feedbackDetails?.clientFeedback?.vote
        ) {
            addOrUpdateFeedback(comment, vote);           
        }
        setIsFeedbackModalOpen(false);
    };

    const handleFeedbackClick = () => {
        if (!feedbackDetails.isOnJob) {
            notifications.showModal(
                "info",
                `Unable to give feedback for ${candidateFullName}`,
                NOT_SHARED_JOB_MESSAGE
            );
        } else if (!feedbackDetails.canGiveFeedback) {
            notifications.showModal(
                "info",
                `Unable to give feedback for ${candidateFullName}`,
                JOB_NOT_CORRECT_STATUS_MESSAGE
            );
        } else {
            setIsFeedbackModalOpen(true);
        }
    };

    const noFeedbackListItem = (item: NoFeedback, key: number) => (
        <ListItem
            disableGutters
            alignItems="flex-start"
            className={classes.feedbackItem}
            key={key}
        >
            <ListItemText
                primary={item.clientFullName}
                secondary={
                    ClientUserRole.fromId(item.jobUserTypeId).displayName
                }
            />
            <ListItemSecondaryAction className={classes.feedbackSecondary}>
                <Text variant="body2" className={classes.italicLabel}>
                    awaiting feedback
                </Text>
            </ListItemSecondaryAction>
        </ListItem>
    );

    const feedbackListItem = (item: Feedback, key: number) => (
        <ListItem
            disableGutters
            alignItems="flex-start"
            className={classes.feedbackItem}
            key={key}
        >
            <ListItemAvatar className={classes.buttonContainer}>
                <IconButton
                    color="primary"
                    size="medium"
                    className={classes.iconButton}
                    disabled={!item.canEdit}
                    onClick={handleFeedbackClick}
                >
                    {item.vote ? <ThumbsUpIcon /> : <ThumbsDownIcon />}
                </IconButton>
            </ListItemAvatar>
            <ListItemText
                primary={
                    <>
                        {item.clientFullName}
                        <Text variant="body2" className={classes.label}>
                            {
                                ClientUserRole.fromId(item.jobUserTypeId)
                                    .displayName
                            }
                        </Text>
                    </>
                }
                secondaryTypographyProps={{ variant: "body1" }}
                secondary={
                    <span className={classes.comment}>{item.comment}</span>
                }
            ></ListItemText>
            <ListItemSecondaryAction className={classes.feedbackSecondary}>
                <Text variant="body2" className={classes.label}>
                    {format(item.updateTimestamp, "P")}
                </Text>
            </ListItemSecondaryAction>
        </ListItem>
    );

    return (
        <LayoutContainer>
            <div className={classes.root}>
                {feedbackDetails.canViewFeedback ? (
                    <>
                        {feedbackList && feedbackList.length > 0 && (
                            <List
                                id="candidateFeedbackList"
                                data-cy="candidateFeedbackList"
                                className={classes.feedbackList}
                            >
                                {feedbackList.map((item, i) =>
                                    feedbackListItem(item, i)
                                )}
                            </List>
                        )}

                        {feedbackList &&
                        feedbackList.length > 0 &&
                        clientsWithoutFeedback &&
                        clientsWithoutFeedback.length > 0 ? (
                            <Divider className={classes.divider} />
                        ) : null}

                        {clientsWithoutFeedback &&
                            clientsWithoutFeedback.length > 0 && (
                                <List
                                    id="clientsWithoutFeedback"
                                    data-cy="clientsWithoutFeedbackList"
                                    className={classes.feedbackList}
                                >
                                    {clientsWithoutFeedback.map((item, i) =>
                                        noFeedbackListItem(item, i)
                                    )}
                                </List>
                            )}
                    </>
                ) : null}

                {!feedbackDetails.canViewFeedback &&
                feedbackDetails.canGiveFeedback ? (
                    <div className={classes.provideFeedbackContainer}>
                        <div className={classes.info}>
                            <Text variant="body1">
                                Provide your feedback on this candidate
                                <br />
                                to view what feedback has been given
                            </Text>
                        </div>
                        <Button
                            color="primary"
                            size="small"
                            className={classes.provideFeedbackButton}
                            onClick={() => setIsFeedbackModalOpen(true)}
                            title="provide feedback"
                            data-cy="provideFeedback-button"
                        >
                            provide feedback
                        </Button>
                    </div>
                ) : null}

                {feedbackDetails.canGiveFeedback && isFeedbackModalOpen ? (
                    <FeedbackModal
                        candidateFullName={candidateFullName}
                        onCancel={() => setIsFeedbackModalOpen(false)}
                        onConfirm={handleConfirmFeedback}
                        selectedFeedbackType={selectedFeedbackType}
                        comment={feedbackDetails.clientFeedback?.comment}
                    />
                ) : null}
            </div>
        </LayoutContainer>
    );
};
